import React, { useMemo, useState } from 'react';
import { CheckIcon, AlertTriangleIcon, MinusIcon, PlusIcon, TruckIcon, ShieldCheckIcon, StoreIcon } from 'lucide-react';
export interface SizeOption {
  value: string;
  label: string;
  range: string;
}
export const SIZES: SizeOption[] = [{
  value: 'xxxs',
  label: 'XXX-Small',
  range: '210-215mm'
}, {
  value: 'xxs',
  label: 'XX-Small',
  range: '220-225mm'
}, {
  value: 'xs',
  label: 'X-Small',
  range: '230-235mm'
}, {
  value: 's',
  label: 'Small',
  range: '240-245mm'
}, {
  value: 'm',
  label: 'Medium',
  range: '250-255mm'
}, {
  value: 'l',
  label: 'Large',
  range: '260-265mm'
}, {
  value: 'xl',
  label: 'X-Large',
  range: '270-275mm'
}, {
  value: 'xxl',
  label: 'XX-Large',
  range: '280-285mm'
}, {
  value: 'xxxl',
  label: 'XXX-Large',
  range: '290-295mm'
}, {
  value: 'xxxxl',
  label: 'XXXX-Large',
  range: '300-305mm'
}];
const FEATURES = ['Additional sensors added for improved accuracy with E-Headgear.', 'Improved design for more robust and accurate detection.', 'To be used with Gen3 Electronic Protector E-Hogu only.', 'Official Daedo Gen3 sensor sock for competition and training.'];
export const BASE_PRICE = 110.84;
export const ENGRAVING_PRICE_PER_PAIR = 10;
/**
 * Cross-section signal: when the user clicks "View suggested sizing chart"
 * inside this card, we broadcast a window event that the Tabs section
 * listens for to switch itself to the sizing tab.
 * Keeps both sections fully self-contained (no shared props from a parent).
 */
export const VIEW_SIZING_CHART_EVENT = 'gen3efoot:view-sizing-chart';
/**
 * Right-side purchase card for the Gen3 E-Foot Gear page.
 * Owns all of its own state — does not accept any props.
 */
export function PurchaseCard() {
  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    SIZES.forEach(s => {
      initial[s.value] = 0;
    });
    return initial;
  });
  const [engravingEnabled, setEngravingEnabled] = useState(false);
  const [nameMode, setNameMode] = useState<'same' | 'different'>('same');
  const [sameName, setSameName] = useState('');
  const [perPairNames, setPerPairNames] = useState<Record<string, string[]>>({});
  const totalPairs = useMemo(() => Object.values(quantities).reduce((sum, n) => sum + n, 0), [quantities]);
  const productSubtotal = totalPairs * BASE_PRICE;
  const engravingTotal = engravingEnabled ? totalPairs * ENGRAVING_PRICE_PER_PAIR : 0;
  const grandTotal = productSubtotal + engravingTotal;
  const setSizeQty = (value: string, qty: number) => {
    setQuantities(prev => ({
      ...prev,
      [value]: Math.max(0, qty)
    }));
  };
  const incrementSize = (value: string) => setSizeQty(value, (quantities[value] || 0) + 1);
  const decrementSize = (value: string) => setSizeQty(value, (quantities[value] || 0) - 1);
  const updatePerPairName = (sizeValue: string, pairIdx: number, val: string) => {
    setPerPairNames(prev => {
      const arr = prev[sizeValue] ? [...prev[sizeValue]] : [];
      arr[pairIdx] = val;
      return {
        ...prev,
        [sizeValue]: arr
      };
    });
  };
  const handleViewSizingChart = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(VIEW_SIZING_CHART_EVENT));
    }
  };
  return <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-427">
      <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full mb-3" data-id="element-428">
        Gen 3
      </span>

      <h1 className="text-3xl md:text-4xl font-black text-red-600 leading-tight mb-3" data-id="element-429">
        Gen3 E-Foot Gear
      </h1>

      <div className="mb-6" data-id="element-430">
        <p className="text-base text-gray-600 mb-4" data-id="element-431">
          The official Daedo Gen3 sensor sock — built for accurate detection in
          competition and serious training with Gen3 E-Hogus.
        </p>

        <ul className="space-y-2.5 mb-4" data-id="element-432">
          {FEATURES.map((feature, idx) => <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-700" data-id="element-433">
              <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-434" />
              <span data-id="element-435">{feature}</span>
            </li>)}
        </ul>

        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4" data-id="element-436">
          <AlertTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-437" />
          <p className="text-sm font-bold text-red-700 uppercase tracking-wide leading-tight" data-id="element-438">
            Do not purchase unless you know you are using Gen 3 E-Hogus
          </p>
        </div>
      </div>

      <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-gray-100" data-id="element-439">
        <span className="text-4xl font-black text-gray-900" data-id="element-440">
          ${BASE_PRICE.toFixed(2)}
        </span>
        <span className="text-sm text-gray-500" data-id="element-441">USD per pair</span>
      </div>

      {/* Sizes & quantities */}
      <div className="mb-6" data-id="element-442">
        <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap" data-id="element-443">
          <h2 className="text-base font-bold text-gray-900" data-id="element-444">
            Choose Sizes & Quantities
          </h2>
          <button type="button" onClick={handleViewSizingChart} className="text-xs text-blue-600 hover:text-blue-800 underline" data-id="element-445">
            Not sure? View suggested sizing chart
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100" data-id="element-446">
          {SIZES.map(s => <SizeRow key={s.value} size={s} qty={quantities[s.value] || 0} onIncrement={() => incrementSize(s.value)} onDecrement={() => decrementSize(s.value)} onChange={val => setSizeQty(s.value, val)} data-id="element-447" />)}
        </div>
      </div>

      {/* Order summary */}
      <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 mb-6" data-id="element-448">
        <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider" data-id="element-449">
          Order Summary
        </h3>
        <div className="space-y-2 text-sm" data-id="element-450">
          <div className="flex justify-between" data-id="element-451">
            <span className="text-gray-600" data-id="element-452">Selected pairs</span>
            <span className="font-semibold text-gray-900" data-id="element-453">{totalPairs}</span>
          </div>
          <div className="flex justify-between" data-id="element-454">
            <span className="text-gray-600" data-id="element-455">Product total</span>
            <span className="font-semibold text-gray-900" data-id="element-456">
              ${productSubtotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-457">
            <span className="text-gray-600" data-id="element-458">Laser engraving</span>
            <span className="font-semibold text-gray-900" data-id="element-459">
              {engravingEnabled ? `+$${engravingTotal.toFixed(2)}` : 'Not added'}
            </span>
          </div>
          <div className="flex justify-between pt-2 mt-2 border-t border-gray-200" data-id="element-460">
            <span className="font-bold text-gray-900" data-id="element-461">Total</span>
            <span className="font-black text-lg text-gray-900" data-id="element-462">
              ${grandTotal.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Personalization */}
      <div className="mb-6" data-id="element-463">
        <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider" data-id="element-464">
          Personalization
        </h3>

        <label className="flex items-start gap-3 cursor-pointer p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors" data-id="element-465">
          <input type="checkbox" checked={engravingEnabled} onChange={e => setEngravingEnabled(e.target.checked)} className="mt-0.5 w-5 h-5 accent-red-600 cursor-pointer" data-id="element-466" />
          <span className="flex-1" data-id="element-467">
            <span className="block text-sm font-semibold text-gray-900" data-id="element-468">
              Add name laser engraving{' '}
              <span className="text-red-600" data-id="element-469">
                (+${ENGRAVING_PRICE_PER_PAIR} per pair, final sale)
              </span>
            </span>
            <span className="block text-xs text-gray-500 mt-1" data-id="element-470">
              Lasered items are final sale. Up to 20 characters per name.
            </span>
          </span>
        </label>

        {engravingEnabled && <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-4" data-id="element-471">
            <div className="flex flex-col sm:flex-row gap-2" data-id="element-472">
              <ModeRadio active={nameMode === 'same'} onClick={() => setNameMode('same')} label="Same name on all pairs" data-id="element-473" />
              <ModeRadio active={nameMode === 'different'} onClick={() => setNameMode('different')} label="Different names per pair" data-id="element-474" />
            </div>

            {nameMode === 'same' && <div data-id="element-475">
                <label htmlFor="same-name-input" className="block text-xs font-bold text-gray-700 mb-1.5" data-id="element-476">
                  Name to laser on all selected pairs
                </label>
                <input id="same-name-input" type="text" value={sameName} onChange={e => setSameName(e.target.value)} placeholder="Enter name (max 20 characters)" maxLength={20} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-477" />
              </div>}

            {nameMode === 'different' && <div className="space-y-3" data-id="element-478">
                {totalPairs === 0 ? <p className="text-xs text-gray-500 italic" data-id="element-479">
                    Select at least one pair above to enter names.
                  </p> : SIZES.filter(s => (quantities[s.value] || 0) > 0).map(s => <div key={s.value} data-id="element-480">
                        <p className="text-xs font-bold text-gray-700 mb-1.5" data-id="element-481">
                          {s.label}
                        </p>
                        <div className="space-y-1.5" data-id="element-482">
                          {Array.from({
                length: quantities[s.value]
              }).map((_, pairIdx) => <input key={pairIdx} type="text" value={perPairNames[s.value]?.[pairIdx] || ''} onChange={e => updatePerPairName(s.value, pairIdx, e.target.value)} placeholder={`Pair ${pairIdx + 1} — name`} maxLength={20} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-483" />)}
                        </div>
                      </div>)}
              </div>}
          </div>}
      </div>

      <a href="/shop/gen-3/" className="w-full inline-block bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold text-base uppercase tracking-wide py-4 rounded-xl transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-4 focus:ring-red-200 text-center" data-id="element-484">
        Add Selected Items To Cart
      </a>

      <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100 text-sm" data-id="element-485">
        <span className="font-semibold text-gray-900" data-id="element-486">Dimensions</span>
        <span className="text-gray-600" data-id="element-487">12 x 4 x 2.5</span>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-gray-100" data-id="element-488">
        <TrustItem icon={<StoreIcon className="w-4 h-4" data-id="element-490" />} label="Official U.S. Distributor" data-id="element-489" />
        <TrustItem icon={<ShieldCheckIcon className="w-4 h-4" data-id="element-492" />} label="Competition support" data-id="element-491" />
        <TrustItem icon={<TruckIcon className="w-4 h-4" data-id="element-494" />} label="Shipping available" data-id="element-493" />
      </div>
    </div>;
}
/* ---- Local helpers (private to this section) ---- */
function SizeRow({
  size,
  qty,
  onIncrement,
  onDecrement,
  onChange
}: {
  size: SizeOption;
  qty: number;
  onIncrement: () => void;
  onDecrement: () => void;
  onChange: (val: number) => void;
}) {
  const active = qty > 0;
  return <div className={`flex items-center justify-between gap-3 px-4 py-3 transition-colors ${active ? 'bg-red-50/40' : 'bg-white hover:bg-gray-50'}`} data-id="element-495">
      <div className="flex-1 min-w-0" data-id="element-496">
        <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-497">
          {size.label}
        </p>
        <p className="text-xs text-gray-500" data-id="element-498">{size.range}</p>
      </div>
      <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white" data-id="element-499">
        <button type="button" onClick={onDecrement} disabled={qty === 0} className="px-2.5 py-1.5 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label={`Decrease ${size.label} quantity`} data-id="element-500">
          <MinusIcon className="w-4 h-4" data-id="element-501" />
        </button>
        <input type="number" min={0} value={qty} onChange={e => onChange(parseInt(e.target.value) || 0)} className="w-12 text-center font-semibold text-gray-900 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" aria-label={`${size.label} quantity`} data-id="element-502" />
        <button type="button" onClick={onIncrement} className="px-2.5 py-1.5 hover:bg-gray-100 transition-colors" aria-label={`Increase ${size.label} quantity`} data-id="element-503">
          <PlusIcon className="w-4 h-4" data-id="element-504" />
        </button>
      </div>
    </div>;
}
function ModeRadio({
  active,
  onClick,
  label
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return <button type="button" onClick={onClick} className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg border-2 transition-all ${active ? 'border-red-600 bg-white text-red-700' : 'border-transparent bg-white text-gray-700 hover:border-gray-300'}`} data-id="element-505">
      <span className="inline-flex items-center gap-2" data-id="element-506">
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? 'border-red-600' : 'border-gray-400'}`} data-id="element-507">
          {active && <span className="w-2 h-2 rounded-full bg-red-600" data-id="element-508" />}
        </span>
        {label}
      </span>
    </button>;
}
function TrustItem({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <div className="flex flex-col items-center text-center gap-1.5" data-id="element-509">
      <div className="text-gray-700" data-id="element-510">{icon}</div>
      <span className="text-[11px] font-medium text-gray-600 leading-tight" data-id="element-511">
        {label}
      </span>
    </div>;
}